import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Anonymous session id — minted per-browser and stored in localStorage.
// Used by public endpoints (currently Soundcheck) so an unauthenticated
// visitor still has a stable identity that scopes their private data to
// this browser only. Never sent for the /auth/* endpoints where the
// server should never care.
function getOrCreateAnonSessionId() {
  try {
    let id = localStorage.getItem('anon_session_id');
    if (!id) {
      // Prefer crypto.randomUUID; fall back for very old browsers.
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('anon_session_id', id);
    }
    return id;
  } catch {
    return null;
  }
}

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always attach the anon session id when we have one — the server
    // only reads it on endpoints that explicitly opt in (Soundcheck).
    const anonId = getOrCreateAnonSessionId();
    if (anonId) {
      config.headers['X-Anon-Session'] = anonId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors. Skip the auto-redirect for Soundcheck endpoints —
// those are public and a stale JWT should silently fall through to the
// anon-session path instead of bouncing an unauthenticated visitor to
// /login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isPublicEndpoint = url.startsWith('/soundcheck') || url.startsWith('soundcheck');
      if (!isPublicEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password });

export const getCurrentUser = () =>
  api.get('/auth/me');

export const getUsers = () =>
  api.get('/auth/users');

// Projects
export const getProjects = (params) =>
  api.get('/projects', { params });

export const getProject = (id) =>
  api.get(`/projects/${id}`);

export const createProject = (data) =>
  api.post('/projects', data);

export const updateProject = (id, data) =>
  api.put(`/projects/${id}`, data);

export const advancePhase = (id) =>
  api.post(`/projects/${id}/advance-phase`);

export const setProjectPhase = (id, phase) =>
  api.post(`/projects/${id}/set-phase`, { phase });

export const generateRaciTasks = (id, phase = null) =>
  api.post(`/projects/${id}/generate-tasks${phase ? `?phase=${phase}` : ''}`);

export const getProjectStats = () =>
  api.get('/projects/stats/overview');

export const getBucketStats = () =>
  api.get('/projects/stats/buckets');

export const getExecutiveSummary = () =>
  api.get('/projects/stats/executive-summary');

export const getHealthSummary = () =>
  api.get('/projects/health-summary');

export const getProjectDeals = (id) =>
  api.get(`/projects/${id}/deals`);

export const getProjectProposal = (id) =>
  api.get(`/projects/${id}/proposal`);

// Proposal regeneration (PM/Management) — re-syncs D-Tools and snapshots
// the result, tying it to the approved COs being incorporated.
export const regenerateProposal = (id, pmNote = null) =>
  api.post(`/projects/${id}/regenerate-proposal`, { pm_note: pmNote });

export const getProposalRegenerationHistory = (id) =>
  api.get(`/projects/${id}/regenerate-proposal/history`);

// Assignments
export const getAssignmentSummary = () =>
  api.get('/projects/assignment-summary');

export const getAssignmentList = (params) =>
  api.get('/projects/assignment-list', { params });

export const assignProject = (id, data) =>
  api.put(`/projects/${id}/assign`, data);

export const bulkAssignProjects = (data) =>
  api.put('/projects/bulk-assign', data);

// Owner-only login activity report (Tom). Returns logged-in users and the
// active employees who haven't logged in over the chosen lookback window.
export const getLoginActivity = (days = 14) =>
  api.get('/admin/login-activity', { params: { days } });

// Owner-only per-user page-view breakdown.
export const getUserPageViews = (userId, days = 14) =>
  api.get(`/admin/page-views/user/${userId}`, { params: { days } });

// Owner-only aggregate "what's everyone using" view.
export const getPageViewsAggregate = (days = 14) =>
  api.get('/admin/page-views/aggregate', { params: { days } });

// Project Aliases (alternate names: street address, city, LLC, AR code, etc.)
export const getProjectAliases = (projectId) =>
  api.get(`/projects/${projectId}/aliases`);

export const addProjectAlias = (projectId, data) =>
  api.post(`/projects/${projectId}/aliases`, data);

export const deleteProjectAlias = (projectId, aliasId) =>
  api.delete(`/projects/${projectId}/aliases/${aliasId}`);

// Project Contacts
export const getProjectContacts = (projectId) =>
  api.get(`/projects/${projectId}/contacts`);

export const createProjectContact = (projectId, data) =>
  api.post(`/projects/${projectId}/contacts`, data);

export const updateProjectContact = (projectId, contactId, data) =>
  api.put(`/projects/${projectId}/contacts/${contactId}`, data);

export const deleteProjectContact = (projectId, contactId) =>
  api.delete(`/projects/${projectId}/contacts/${contactId}`);

// Calendar
export const getCalendarProjects = (params) =>
  api.get('/calendar/projects', { params });

// AR Report (management only)
export const getArRecords = (params) =>
  api.get('/ar', { params });

export const getArPayments = () =>
  api.get('/ar/payments');

export const getArSummary = () =>
  api.get('/ar/summary');

export const syncArReport = () =>
  api.post('/ar/sync');

// AR Collections drilldown (any past month Sage retains)
export const getArCollections = (params) =>
  api.get('/ar/collections', { params });

export const getArCollectionsMonths = () =>
  api.get('/ar/collections/months');

// Labor Variance — Finance & Mgmt
export const getLaborVariance = (params) =>
  api.get('/labor-variance', { params });

// New Business by Salesperson — Finance & Mgmt
export const getNewBusiness = (params) =>
  api.get('/new-business', { params });

// 2026-05-18 (Chris Georghiou): server-side .xlsx export. Mirrors the
// on-screen "By Salesman" view with grouped/subtotaled detail.
export const exportNewBusinessXlsx = (params) =>
  api.get('/new-business/export', { params, responseType: 'blob' });

// Zoho CRM
export const getCrmSyncStatus = () =>
  api.get('/zoho/crm/status');

export const triggerCrmSync = () =>
  api.get('/zoho/crm/sync', { responseType: 'text' });

// Tasks
export const getMyTasks = (params) =>
  api.get('/tasks/my-tasks', { params });

export const getAwaitingApproval = () =>
  api.get('/tasks/awaiting-approval');

// Unified "things assigned to me" feed (work items + install tasks + dispatch).
// Tom Clancy 2026-05-14.
export const getMyAssignments = () =>
  api.get('/tasks/my-assignments');

export const getTask = (id) =>
  api.get(`/tasks/${id}`);

export const updateTaskStatus = (id, status) =>
  api.put(`/tasks/${id}/status`, { status });

export const approveTask = (id) =>
  api.post(`/tasks/${id}/approve`);

export const createTask = (data) =>
  api.post('/tasks', data);

export const getTaskStats = () =>
  api.get('/tasks/stats/dashboard');

// Change Orders
export const getChangeOrders = (params) =>
  api.get('/change-orders', { params });

export const getChangeOrder = (id) =>
  api.get(`/change-orders/${id}`);

export const createChangeOrder = (data) =>
  api.post('/change-orders', data);

export const updateChangeOrderStatus = (id, status) =>
  api.put(`/change-orders/${id}/status`, { status });

// Notifications
export const getNotifications = (params) =>
  api.get('/notifications', { params });

export const getUnreadCount = () =>
  api.get('/notifications/unread-count');

export const markNotificationRead = (id) =>
  api.put(`/notifications/${id}/read`);

export const markAllRead = () =>
  api.put('/notifications/read-all');

export const deleteNotification = (id) =>
  api.delete(`/notifications/${id}`);

// Labor
export const getCompanyLabor = (params) =>
  api.get('/labor/summary', { params });

export const getProjectLabor = (id) =>
  api.get(`/labor/project/${id}`);

export const getLaborUtilization = () =>
  api.get('/labor/utilization');

export const getLaborUtilizationByPM = (pm) =>
  api.get(`/labor/utilization/${encodeURIComponent(pm)}`);

// Dispatch
export const getDispatchAssignments = (start, end) =>
  api.get('/dispatch', { params: { start, end } });

export const getDispatchTechnicians = () =>
  api.get('/dispatch/technicians');

export const searchDispatchTargets = (search, limit = 50) =>
  api.get('/dispatch/targets', { params: { search, limit } });

// Comprehensive dispatch search — hits every project ever (active,
// closed, cancelled, on-hold) plus every AfterCare service site.
// Used by the dispatch calendar search bar (Tom Clancy 2026-08-06).
export const searchDispatchAll = (q, limit = 60) =>
  api.get('/dispatch/search', { params: { q, limit } });

export const createDispatchAssignment = (data) =>
  api.post('/dispatch', data);

export const updateDispatchAssignment = (id, data) =>
  api.put(`/dispatch/${id}`, data);

export const deleteDispatchAssignment = (id) =>
  api.delete(`/dispatch/${id}`);

export const sendDispatchEmail = (date) =>
  api.post('/dispatch/send-email', { date });

// Field Notes
export const getFieldNotes = (projectId) =>
  api.get(`/projects/${projectId}/field-notes`);

export const createFieldNote = (projectId, data) =>
  api.post(`/projects/${projectId}/field-notes`, data);

// Work Items
export const getWorkItems = (projectId) =>
  api.get(`/projects/${projectId}/work-items`);

export const createWorkItem = (projectId, data) =>
  api.post(`/projects/${projectId}/work-items`, data);

export const updateWorkItem = (projectId, itemId, data) =>
  api.put(`/projects/${projectId}/work-items/${itemId}`, data);

export const deleteWorkItem = (projectId, itemId) =>
  api.delete(`/projects/${projectId}/work-items/${itemId}`);

// User Preferences
export const getPreferences = () =>
  api.get('/auth/preferences');

export const updatePreferences = (prefs) =>
  api.put('/auth/preferences', prefs);

// Client Messages (internal ACS team)
export const getClientMessagesList = () => api.get('/client-messages');
export const getClientMessages = (arCode) => api.get(`/client-messages/${arCode}`);
export const sendClientMessage = (arCode, message) => api.post(`/client-messages/${arCode}`, { message });

export default api;

// Client Portal Browse (management only)
export const browseClients = () => api.get('/client/browse');
export const browseClientDetail = (arCode) => api.get(`/client/browse?ar_code=${arCode}`);

// Timeline Gap Enforcement
export const getTimelineGaps = () => api.get('/projects/my-timeline-gaps');
export const bulkTimelineUpdate = (updates) => api.put('/projects/bulk-timeline-update', { updates });

// Company Timeline (management only)
export const getCompanyTimeline = () => api.get('/projects/company-timeline');

// Labor Utilization Dashboard (management only)
export const getLaborUtilizationSummary = (params) => api.get('/labor-utilization/summary', { params });
export const getITTeamUtilizationSummary = (params) => api.get('/it-team-utilization/summary', { params });
export const getITTeamUtilizationEmployee = (name, params) =>
  api.get(`/it-team-utilization/employee/${encodeURIComponent(name)}`, { params });
export const getLaborUtilizationEmployee = (name, params) =>
  api.get(`/labor-utilization/employee/${encodeURIComponent(name)}`, { params });

// CRM
export const getCRMContacts = (params) => api.get('/crm/contacts', { params });
export const getCRMReps = () => api.get('/crm/reps');
export const getDToolsDirectory = (params) => api.get('/crm/dtools-directory', { params });
export const getZohoContacts = (params) => api.get('/crm/zoho-contacts', { params });
export const getZohoContactTypes = () => api.get('/crm/zoho-contacts/types');
export const syncZohoContacts = () => api.post('/crm/zoho-contacts/sync');
export const getUnifiedContacts = (params) => api.get('/crm/unified', { params });
export const getZohoReps = () => api.get('/crm/zoho-contacts/reps');
export const getCRMCompanies = (params) => api.get('/crm/companies', { params });
export const getCompanyReps = (companyKey) => api.get(`/crm/company-reps/${encodeURIComponent(companyKey)}`);
export const assignCompanyRep = (data) => api.post('/crm/company-reps', data);
export const removeCompanyRep = (id) => api.delete(`/crm/company-reps/${id}`);
export const getCompanyTypes = () => api.get('/crm/types');
export const addCompanyType = (data) => api.post('/crm/types', data);
export const setCompanyType = (data) => api.put('/crm/company-type', data);

// Manual CRM contacts (global_contacts)
export const getGlobalContacts = (params) => api.get('/crm/global-contacts', { params });
export const createGlobalContact = (data) => api.post('/crm/global-contacts', data);
export const updateGlobalContact = (id, data) => api.put(`/crm/global-contacts/${id}`, data);
export const deleteGlobalContact = (id) => api.delete(`/crm/global-contacts/${id}`);

// Mailing list builder (filtered + email-deduped slice of /unified)
export const getMailingList = (params) => api.get('/crm/mailing-list', { params });
export const getCompaniesForMailing = () => api.get('/crm/companies-for-mailing');
export const bulkImportContacts = (data) => api.post('/crm/contacts/bulk-import', data);
export const patchContact = (id, data) => api.patch(`/crm/contacts/${id}`, data);

// Messaging
export const getChannels = () => api.get('/messaging/channels');
export const getMessages = (channelId, params) => api.get(`/messaging/channels/${channelId}/messages`, { params });
export const sendMessage = (channelId, content) => api.post(`/messaging/channels/${channelId}/messages`, { content });
export const createChannel = (data) => api.post('/messaging/channels', data);
export const createDM = (userId) => api.post('/messaging/dm', { user_id: userId });
export const markRead = (channelId) => api.put(`/messaging/channels/${channelId}/read`);
export const getChannelMembers = (channelId) => api.get(`/messaging/channels/${channelId}/members`);
export const addChannelMembers = (channelId, userIds) => api.post(`/messaging/channels/${channelId}/members`, { user_ids: userIds });
export const removeChannelMember = (channelId, userId) => api.delete(`/messaging/channels/${channelId}/members/${userId}`);
export const getMessagingUsers = () => api.get('/messaging/users');
export const getProjectChannel = (projectId) => api.get(`/messaging/project-channel/${projectId}`);

// Sales Pipeline
export const getSalesPipeline = () => api.get('/sales-pipeline');
export const exportSalesPipelineXlsx = () =>
  api.get('/sales-pipeline/export.xlsx', { responseType: 'blob' });
export const getSalesForecast = () => api.get('/sales-pipeline/forecast');
export const exportSalesForecastXlsx = () =>
  api.get('/sales-pipeline/forecast.xlsx', { responseType: 'blob' });
export const getManufacturerPurchases = (years = 3) =>
  api.get('/manufacturer-purchases', { params: { years } });
export const getManufacturerPurchasesAccess = () =>
  api.get('/manufacturer-purchases/access');
export const getSalesPipelineDeal = (id) => api.get(`/sales-pipeline/${id}`);
export const listSalesFollowups = (dealId) =>
  api.get(`/sales-pipeline/${encodeURIComponent(dealId)}/followups`);
export const createSalesFollowup = (dealId, data) =>
  api.post(`/sales-pipeline/${encodeURIComponent(dealId)}/followups`, data);
export const deleteSalesFollowup = (dealId, followupId) =>
  api.delete(`/sales-pipeline/${encodeURIComponent(dealId)}/followups/${followupId}`);

// Installation Tasks
// Pass `completionDate` (YYYY-MM-DD) to persist the project's completion
// date inline if the user's separate save didn't land. Belt-and-suspenders
// for the 'Set a project completion date before generating tasks' bug.
export const generateInstallationTasks = (id, completionDate = null) =>
  api.post(`/projects/${id}/generate-installation-tasks`,
    completionDate ? { completion_date: completionDate } : {});
export const getInstallationTasks = (id) => api.get(`/projects/${id}/installation-tasks`);
export const updateInstallationTask = (id, taskId, data) => api.put(`/projects/${id}/installation-tasks/${taskId}`, data);
export const bulkAssignTasks = (id, data) => api.put(`/projects/${id}/installation-tasks/bulk-assign`, data);

// RACI Task Assignment
export const assignRaciTask = (projectId, taskId, data) =>
  api.post(`/projects/${projectId}/tasks/${taskId}/assign`, data);

// Global Contacts Directory (legacy — in-app cache of past project contacts)
export const searchGlobalContacts = (search) =>
  api.get('/projects/contacts/global', { params: { search } });

// Native CRM lookup — searches the Zoho CRM mirror (Contacts + Accounts).
// When a result is selected, pass its zoho_id back via createProjectContact
// so the project contact stays linked to the source CRM record.
export const searchCrmContacts = (projectId, search) =>
  api.get(`/projects/${projectId}/contacts/crm-search`, { params: { search } });

// Daily Field Reports
export const getDailyReports = (params) => api.get('/daily-reports', { params });
export const getDailyReport = (id) => api.get(`/daily-reports/${id}`);
export const createDailyReport = (data) => api.post('/daily-reports', data);
export const updateDailyReport = (id, data) => api.put(`/daily-reports/${id}`, data);
export const submitDailyReport = (id) => api.put(`/daily-reports/${id}/submit`);
export const reviewDailyReport = (id) => api.put(`/daily-reports/${id}/review`);
export const getDailyReportSummary = (params) => api.get('/daily-reports/summary', { params });
export const getDailyReportMyProjects = () => api.get('/daily-reports/my-projects');
export const getDailyReportsRecent = (params) => api.get('/daily-reports/recent', { params });
export const previewAutoDailyReport = (params) =>
  api.get('/daily-reports/auto-generate/preview', { params });
export const autoGenerateDailyReport = (data) =>
  api.post('/daily-reports/auto-generate', data);

// ── Field Ops (technician daily queue) ────────────────────────────────────
export const getFieldOpsToday = (params) =>
  api.get('/field-ops/today', { params });
export const startFieldOpsTask = (taskId) =>
  api.post(`/field-ops/tasks/${taskId}/start`);
export const completeFieldOpsTask = (taskId, data) =>
  api.post(`/field-ops/tasks/${taskId}/complete`, data || {});
export const blockFieldOpsTask = (taskId, data) =>
  api.post(`/field-ops/tasks/${taskId}/block`, data || {});
export const uploadReportPhotos = (id, formData) =>
  api.post(`/daily-reports/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Demo Mode
export const getDemoMode = () => api.get('/auth/demo-mode');
export const toggleDemoMode = () => api.post('/auth/demo-mode');


// Sales Follow-ups
export const getSalesFollowups = () => api.get('/auth/sales-followups');

// Clients (unified SOW view)
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (arCode) => api.get(`/clients/${encodeURIComponent(arCode)}`);
export const getClientContacts = (arCode) => api.get(`/clients/${encodeURIComponent(arCode)}/contacts`);
export const assignClientSales = (arCode, sales_id) => api.put(`/clients/${encodeURIComponent(arCode)}/assign-sales`, { sales_id });

// Meeting Notes
export const getMeetingNotes = (params) => api.get('/meetings', { params });
export const getMeetingNote = (id) => api.get(`/meetings/${id}`);
export const getProjectMeetings = (projectId) => api.get(`/meetings/project/${projectId}`);
export const getUnlinkedMeetings = () => api.get('/meetings/unlinked');
export const getUnlinkedMeetingsCount = () => api.get('/meetings/unlinked-count');
export const linkMeeting = (id, projectId) => api.put(`/meetings/${id}/link`, { project_id: projectId });
export const addManualMeeting = (data) => api.post('/meetings/manual', data);
export const deleteMeeting = (id) => api.delete(`/meetings/${id}`);
export const getMeetingToken = () => api.get('/auth/meeting-token');
export const generateMeetingToken = () => api.post('/auth/meeting-token');

// AfterCare (Sage SM)
export const getAftercareDashboard = () => api.get('/aftercare/dashboard');
export const getAftercareOperations = (windowDays = 90) => api.get('/aftercare/operations', { params: { window_days: windowDays } });
export const getAftercareWorkOrders = (params) => api.get('/aftercare/work-orders', { params });
export const getAftercareWorkOrder = (id) => api.get(`/aftercare/work-orders/${id}`);
export const getAftercareClients = (params) => api.get('/aftercare/clients', { params });
export const getAftercareClient = (arcust) => api.get(`/aftercare/clients/${encodeURIComponent(arcust)}`);
export const getAftercareTechnicians = () => api.get('/aftercare/technicians');
export const getAftercareCoverage = () => api.get('/aftercare/coverage');
export const getClientWarranty = () => api.get('/aftercare/client-warranty');

// Closed-jobs archive: completed projects that have left WIP but whose
// Command Center page must remain accessible forever. Tom Clancy 2026-05-14.
export const getClosedJobs = (params) => api.get('/aftercare/closed-jobs', { params });
export const updateClientWarranty = (ar, patch) => api.patch(`/aftercare/client-warranty/${encodeURIComponent(ar)}`, patch);
export const updateAfterCareWarranty = (ar, patch) => api.patch(`/aftercare/client-warranty/${encodeURIComponent(ar)}/aftercare`, patch);

// AfterCare intake queue (Tom 2026-05-20) — projects marked Complete in
// D-Tools that haven't been formally handed off to AfterCare yet.
export const getAftercareIntakeQueue = (params) => api.get('/aftercare/intake-queue', { params });
export const dismissAftercareIntake = (projectId, reason) =>
  api.post(`/aftercare/intake-queue/${projectId}/dismiss`, { reason });
export const undoAftercareIntakeDismissal = (projectId) =>
  api.delete(`/aftercare/intake-queue/${projectId}/dismiss`);

// PM Dashboard
export const getPMDashboard = (pmId) => api.get('/pm-dashboard', { params: pmId ? { pm_id: pmId } : {} });
export const addFieldNote = (projectId, data) => api.post(`/projects/${projectId}/field-notes`, data);

// Unbillable Labor
export const getUnbillableLabor = (projectId) => api.get(`/projects/${projectId}/unbillable-labor`);
export const addUnbillableLabor = (projectId, data) => api.post(`/projects/${projectId}/unbillable-labor`, data);
export const deleteUnbillableLabor = (projectId, entryId) => api.delete(`/projects/${projectId}/unbillable-labor/${entryId}`);

// Project Documents
export const getProjectDocuments = (projectId) => api.get(`/projects/${projectId}/documents`);
export const uploadProjectDocument = (projectId, formData) => api.post(`/projects/${projectId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteProjectDocument = (projectId, docId) => api.delete(`/projects/${projectId}/documents/${docId}`);

// Engagement Layer (feature-flagged on server)
export const getEngagements = (params) => api.get('/engagements', { params });
export const getEngagement = (id) => api.get(`/engagements/${id}`);
export const updateEngagement = (id, data) => api.patch(`/engagements/${id}`, data);

// Closed Jobs Report (Finance / Exec Mgmt)
export const getClosedJobsFilters = () => api.get('/closed-jobs/filters');
export const getClosedJobsSummary = (params) => api.get('/closed-jobs/summary', { params });
export const getClosedJobsByCategory = (params) => api.get('/closed-jobs/by-category', { params });
export const getClosedJobsBySalesman = (params) => api.get('/closed-jobs/by-salesman', { params });
export const getClosedJobsByPM = (params) => api.get('/closed-jobs/by-pm', { params });
export const getClosedJobsList = (params) => api.get('/closed-jobs/jobs', { params });


// Product Search (Tom 2026-06-19) — search D-Tools line items across all projects
export const productSearchStats = () => api.get('/product-search/stats');
export const productSearchLookup = (q, scope = 'sold', limit = 200) =>
  api.get('/product-search/lookup', { params: { q, scope, limit } });
export const productSearchJobs = (params) =>
  api.get('/product-search/jobs', { params });

// Active WIP / Active Projects (Tom 2026-08-05) — curated view of the ~197
// projects that are actually being worked on today (vs. the ~316 that Sage
// still marks 'In progress'). Powers the /wip/active page and PM date entry.
export const getActiveWip       = () => api.get('/wip/active');
export const getInactiveWip     = () => api.get('/wip/inactive');
export const setProjectActiveWip = (projectId, is_active_wip, note) =>
  api.patch(`/projects/${projectId}/active-wip`, { is_active_wip, note });
export const setProjectPhaseDates = (projectId, dates) =>
  api.patch(`/projects/${projectId}/phase-dates`, dates);
export const getActiveWipAudit  = (projectId) =>
  api.get(`/projects/${projectId}/active-wip-audit`);
export const exportActiveWipUrl = () => '/api/wip/active/export';

// D-Tools CRM (Tom 2026-06-19) — Zoho CRM replacement
export const dtoolsCrmStats     = () => api.get('/dtools-crm/stats');
export const dtoolsCrmStages    = () => api.get('/dtools-crm/stages');
export const dtoolsCrmPipeline  = (params) => api.get('/dtools-crm/pipeline', { params });
export const dtoolsCrmClients   = (params) => api.get('/dtools-crm/clients', { params });
export const dtoolsCrmClient    = (id)     => api.get(`/dtools-crm/clients/${id}`);
export const dtoolsCrmContacts  = (params) => api.get('/dtools-crm/contacts', { params });
